/* eslint-disable max-len */
import React, { useMemo } from 'react';
import { BoardLiteData } from '../../../api/ApiTypes';
import { MyBoardData } from '../../../api/endpoints/boards';
import { LabelValue } from '../../../platform/types/LabelValue';
import { Button } from '../../../ui';
import { SortMode } from '../../common/Sorter';
import { Flexer, Title } from '../elements/LayoutElements';
import { SearchEmpty } from '../templates/gallery/SearchEmpty';
import { useInfiniteBoardData } from './BoardHooks';
import { BoardListContext, BoardListItem } from './BoardListItem';
import { BoardsEmpty } from './BoardsEmpty';
import { Bucket, useDateBuckets } from './BoardsHooks';
import { FolderEmpty } from './FolderEmpty';
import { useLiveBoardsForWorkspace } from './LiveBoards';

type OnSelect = (board: BoardLiteData) => void;

const sortOptions = [
    LabelValue('Last Visited', 'lastSeenAt'),
    LabelValue('Last Updated', 'updatedAt'),
    LabelValue('Date Created', 'createdAt'),
    LabelValue('Alphabetical', 'label'),
];

const sortLabels = {
    'lastSeenAt': 'Visited',
    'updatedAt': 'Updated',
    'createdAt': 'Created',
};

const BoardList = React.memo<{ boards: MyBoardData[] }>(({ boards }) =>
    <Flexer flex="row" wrap="wrap" gap="2">
        {boards.map(x => <BoardListItem key={x.id} board={x} />)}
    </Flexer>
);

const BoardBucket = React.memo<{ bucket: Bucket<MyBoardData> }>(({ bucket }) => {
    if (!bucket.items.length) return null;
    return <Flexer flex="column" gap=".5">
        <Flexer margin=".25">
            <Title flush>{bucket.label}</Title>
        </Flexer>
        <BoardList boards={bucket.items} />
    </Flexer>;
});

const BoardBucketListByDate = React.memo<{ boards: MyBoardData[], sort: SortMode }>(({ boards, sort }) => {
    const buckets = useDateBuckets(boards, sort, sortLabels);
    if (!buckets.length) return null;
    return <Flexer flex="column" gap="2" padding="2 0">
        {buckets.map(u => <BoardBucket key={u.key} bucket={u} />)}
    </Flexer>;
});

const BoardBucketListAZ = React.memo<{ boards: MyBoardData[], sort: SortMode }>(({ boards, sort }) => {
    return <Flexer flex="column" gap=".5">
        <Flexer margin=".25">
            <Title flush>{sort[1] === 'asc' ? 'A-Z' : 'Z-A'}</Title>
        </Flexer>
        <BoardList boards={boards} />
    </Flexer>;
});

const BoardBucketList = React.memo<{ boards: MyBoardData[], sort: SortMode }>(({ boards, sort }) => {
    return (sort[0].endsWith('At'))
        ? <BoardBucketListByDate boards={boards} sort={sort} />
        : <BoardBucketListAZ boards={boards} sort={sort} />;
});

export type MainBoardListViewProps = {
    workspaceId?: string,
    folderId?: string,
    search?: string,
    sort?: SortMode,
    readonly?: boolean,
    onSelect?: OnSelect,
    options?: {
        showWorkspace?: boolean,
    },
};

export const MainBoardListView = React.memo<MainBoardListViewProps>(({ workspaceId, folderId, search, sort, onSelect, options, readonly }) => {

    // const [search, setSearch] = useState('');
    // const [sort, setSort] = useLocalStorage(`deqo/ui/${workspaceId}/sort`, ['lastSeenAt', 'desc'] as SortMode);

    search = search?.trim() || '';
    sort = sort || ['lastSeenAt', 'desc'];

    const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteBoardData({ workspaceId, folderId }, search, sort);
    const liveIds = useLiveBoardsForWorkspace(workspaceId);

    const allBoards = useMemo(() => (data?.pages?.flatMap(x => x.items) ?? []), [data]);

    if (!!allBoards.length) {
        return <BoardListContext.Provider value={{ onSelect, draggable: !readonly, sort, ...(options || {}) }}>
            <Flexer>
                {!!allBoards.length && <>
                    <Flexer flex="column" gap="2">
                        <BoardBucketList boards={allBoards} sort={sort} />
                    </Flexer>
                    {hasNextPage &&
                        <Flexer flex="row" center padding="4">
                            <Button onAction={() => fetchNextPage()}>Load More</Button>
                        </Flexer>
                    }
                </>}
                {!allBoards.length && <>
                    {!isLoading &&
                        <Flexer flex height="32" margin="2 0">
                            {!!search && <SearchEmpty />}
                            {!search && !!folderId && <SearchEmpty />}
                        </Flexer>
                    }
                </>}
            </Flexer>
        </BoardListContext.Provider>;
    }
    else if (!!search) {
        return <SearchEmpty />;
    }
    else if (!!folderId && folderId !== 'root') {
        return <FolderEmpty />;
    }
    else {
        return <BoardsEmpty />;
    }

});
